import { createActions, handleActions } from 'redux-actions';
import { Map } from 'immutable';
import _ from 'lodash';

export const {
  setStream,
  addSocketStream,
  addSendStream,
  addFailStream,
  addFileStream,
  updateFileStream,
  removeFileStream,
  setSendMessageType,
  setSelectTrackId,
  appendBeforeHistoryStreams,
  updateStream,
  clearStreams,
  appendHistoryStream,
  updateStreamReadStatus,
  updateStreamTrusteeshipTag,
  updateStreamTranslate
} = createActions({
  SET_STREAM: action => {
    return action;
  },
  ADD_SOCKET_STREAM: action => {
    return action;
  },
  SET_SELECT_TRACK_ID: action => {
    return action;
  },
  ADD_SEND_STREAM: action => {
    return action;
  },
  ADD_FAIL_STREAM: action => {
    return action;
  },
  ADD_FILE_STREAM: action => {
    return action;
  },
  UPDATE_FILE_STREAM: action => {
    return action;
  },
  REMOVE_FILE_STREAM: action => {
    return action;
  },
  SET_SEND_MESSAGE_TYPE: action => {
    return action;
  },
  APPEND_BEFORE_HISTORY_STREAMS: action => {
    return action;
  },
  UPDATE_STREAM: action => {
    return action;
  },
  CLEAR_STREAMS: action => action,
  APPEND_HISTORY_STREAMS: action => action,
  UPDATE_STREAM_READ_STATUS: action => action,

  // 机器人托管，更新消息的待处理标记
  UPDATE_STREAM_TRUSTEESHIP_TAG: action => action,
  UPDATE_STREAM_TRANSLATE: action => action
});

const initState = Map({
  afterHistoryStreams: [],
  beforeHistoryStreams: [],
  streams: [],
  failStreams: [],
  fileStreams: [],
  historyStreams: [],
  selectTrackId: '',
  messageType: '1',
  beforeConv: null
});

function handleUpdateStreamReadStatus(streams, updateStreams) {
  return streams.map(stream => {
    const updateItem = updateStreams.find(updateStream => updateStream.msg_id === stream.id);
    if (updateItem && (!stream.read_status || stream.read_status < updateItem.read_status)) {
      return {
        ...stream,
        read_status: updateItem.read_status
      };
    }
    return stream;
  });
}

const stream = handleActions(
  {
    [setStream]: (state, action) => {
      const { payload } = action;
      return state.set('streams', payload);
    },
    [setSelectTrackId]: (state, action) => {
      return state.set('selectTrackId', action.payload || '');
    },
    [addSocketStream]: (state, action) => {
      const { payload } = action;
      // React 18 createRoot 自动 batching 会导致旧对话 socket 消息与 clearStreams 合并处理
      // 在 reducer 层按 selectTrackId 过滤，拒绝不属于当前对话的消息
      const currentTrackId = state.get('selectTrackId');
      if (currentTrackId) {
        const filtered = payload.filter(item => item.track_id === currentTrackId);
        if (filtered.length === 0) return state;
        return state.update('streams', data => {
          return _.unionBy(filtered, data, 'id');
        });
      }
      return state.update('streams', data => {
        return _.unionBy(payload, data, 'id');
      });
    },
    [addSendStream]: (state, action) => {
      const { payload } = action;
      return state.update('streams', data => {
        const hasIn = _.some(data, ['id', payload.id]);
        if (hasIn) {
          return data;
        }
        return [...data, payload];
      });
    },
    [addFailStream]: (state, action) => {
      const { payload } = action;
      return state.update('failStreams', data => {
        return [...data, payload];
      });
    },
    [addFileStream]: (state, action) => {
      return state.update('fileStreams', data => {
        const { payload } = action;
        return [...data, payload];
      });
    },
    [updateFileStream]: (state, action) => {
      return state.update('fileStreams', data => {
        const { payload } = action;
        return _.map(data, value => {
          if (value.created_on === payload.created_on) {
            return payload;
          }
          return value;
        });
      });
    },
    [removeFileStream]: (state, action) => {
      return state.update('fileStreams', data => {
        const { payload } = action;
        return _.filter(data, value => {
          return value.created_on !== payload.created_on;
        });
      });
    },
    [setSendMessageType]: (state, action) => {
      const { payload } = action;
      return state.update('messageType', () => payload);
    },
    [appendBeforeHistoryStreams]: (state, action) => {
      const { payload } = action;
      return state.update('beforeHistoryStreams', beforeHistoryStreams => [...payload, ...beforeHistoryStreams]);
    },
    [updateStream]: (state, action) => {
      return state.update('streams', data => {
        const { payload = {} } = action;
        return _.map(data, value => {
          if (value.id === payload.msg_id) {
            const prevExtra = value?.extra ?? {};
            return { ...value, ...{ extra: { ...prevExtra, ...payload } } };
          }
          return value;
        });
      });
    },
    [clearStreams]: state => {
      const exceptKeys = ['messageType', 'selectTrackId'];
      const valueMap = exceptKeys.reduce((prev, now) => {
        return {
          ...prev,
          [now]: state.get(now)
        };
      }, {});
      const newState = exceptKeys.reduce((prev, now) => {
        return prev.set(now, valueMap[now]);
      }, initState);
      return newState;
    },
    [appendHistoryStream]: (state, action) => {
      return state.set('historyStreams', action.payload);
    },
    [updateStreamReadStatus]: (state, action) => {
      const msgs = action.payload;
      if (msgs?.length > 0) {
        return state.update('streams', streams => handleUpdateStreamReadStatus(streams, msgs));
      }
      return state;
    },
    [updateStreamTrusteeshipTag]: (state, { payload }) => {
      return state.update('streams', streams => {
        payload.msg_ids.forEach(id => {
          const targetIndex = streams.findIndex(stream => stream.id === id);
          if (targetIndex > -1) {
            const newStream = { ...streams[targetIndex] };
            if (newStream.extra) {
              newStream.extra.unknown_type = payload.tag;
              newStream.extra.intercept_word = payload.intercept_word || '';
            } else {
              newStream.extra = { unknown_type: payload.tag, intercept_word: payload.intercept_word || '' };
            }
            streams.splice(targetIndex, 1, newStream);
          }
        });
        return [...streams];
      });
    },
    [updateStreamTranslate]: (state, { payload }) => {
      const { msg_id, ...rest } = payload;
      return state.update('streams', streams => {
        return _.map(streams, value => {
          if (value.id === payload.msg_id) {
            const translate = value?.translate ?? {};
            delete translate.fail;
            return { ...value, ...{ translate: { ...translate, ...rest } } };
          }
          return value;
        });
      });
    }
  },
  initState
);

export default stream;
